/**
 * Keystone RFP Agent Theme Configuration
 * Professional teal/blue color scheme inspired by keystoneenvironmental.ca
 */

import { ThemeConfig } from '@tracekit/core';

/**
 * Keystone Environmental Brand Colors
 * Extracted from https://keystoneenvironmental.ca/
 */
export const KEYSTONE_COLORS = {
  // Primary: Dark teal/blue (from header/primary elements)
  primary: '#0e7490', // cyan-700
  primaryLight: '#06b6d4', // cyan-500
  primaryDark: '#164e63', // cyan-900
  
  // Secondary: Pure blue (from CTAs and accents) - Updated per user preference
  secondary: '#3b82f6', // blue-500
  secondaryLight: '#60a5fa', // blue-400
  secondaryDark: '#1d4ed8', // blue-700
  
  // Accent: Medium blue
  accent: '#0891b2', // cyan-600
  
  // Background: Clean white
  background: '#ffffff',
  backgroundAlt: '#f8fafc', // slate-50
  
  // Text: Professional dark gray
  text: '#0f172a', // slate-900
  textSecondary: '#475569', // slate-600
};

/**
 * Keystone Theme Configuration
 * Light mode by default with professional teal/blue colors
 */
export const keystoneThemeConfig: ThemeConfig = {
  defaultMode: 'light',
  allowToggle: true,
  extends: 'keystone', // Use keystone preset as base
  
  // Custom light theme overrides
  light: {
    id: 'keystone-rfp-light',
    name: 'Keystone RFP Light',
    mode: 'light',
    colors: {
      primary: {
        50: '#ecfeff',
        100: '#cffafe',
        200: '#a5f3fc',
        300: '#67e8f9',
        400: '#22d3ee',
        500: '#06b6d4', // Keystone primary light
        600: '#0891b2', // Keystone accent
        700: '#0e7490', // Keystone primary
        800: '#155e75',
        900: '#164e63', // Keystone primary dark
      },
      secondary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa', // Blue secondary light
        500: '#3b82f6', // Blue secondary
        600: '#2563eb',
        700: '#1d4ed8', // Blue secondary dark
        800: '#1e40af',
        900: '#1e3a8a',
      },
      success: {
        light: '#d1fae5',
        main: '#10b981',
        dark: '#059669',
      },
      warning: {
        light: '#fef3c7',
        main: '#f59e0b',
        dark: '#d97706',
      },
      error: {
        light: '#fee2e2',
        main: '#ef4444',
        dark: '#dc2626',
      },
      info: {
        light: '#e0f2fe',
        main: '#0891b2',
        dark: '#0e7490',
      },
      background: {
        default: '#ffffff', // Keystone background
        paper: '#f8fafc', // Keystone background alt
        elevated: '#f1f5f9', // slate-100
      },
      surface: {
        main: '#ffffff',
        hover: '#f8fafc',
        active: '#f1f5f9',
      },
      text: {
        primary: '#0f172a', // Keystone text
        secondary: '#475569', // Keystone text secondary
        disabled: '#94a3b8', // slate-400
        inverse: '#ffffff',
      },
      border: {
        light: '#f1f5f9',
        main: '#e2e8f0',
        dark: '#cbd5e1',
      },
      sidebar: {
        background: 'linear-gradient(180deg, #1e40af 0%, #1d4ed8 50%, #2563eb 100%)', // Pure blue gradient
        text: '#ffffff',
        textSecondary: '#dbeafe', // blue-100
        hover: 'rgba(255, 255, 255, 0.15)',
        active: 'rgba(59, 130, 246, 0.3)', // blue accent for active state
        border: 'rgba(255, 255, 255, 0.1)',
        accent: '#3b82f6', // Pure blue (blue-500)
      },
      header: {
        background: '#ffffff',
        text: '#0f172a',
        border: '#e2e8f0',
      },
      navigation: {
        background: 'transparent',
        text: '#ffffff',
        hover: 'rgba(255, 255, 255, 0.15)',
        active: 'rgba(59, 130, 246, 0.3)', // blue accent
        activeText: '#ffffff',
        border: 'rgba(255, 255, 255, 0.1)',
      },
    },
  },
  
  // Custom dark theme overrides (optional, for users who prefer dark mode)
  dark: {
    id: 'keystone-rfp-dark',
    name: 'Keystone RFP Dark',
    mode: 'dark',
    colors: {
      primary: {
        50: '#ecfeff',
        100: '#cffafe',
        200: '#a5f3fc',
        300: '#67e8f9',
        400: '#22d3ee',
        500: '#06b6d4',
        600: '#0891b2',
        700: '#0e7490',
        800: '#155e75',
        900: '#164e63',
      },
      secondary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
      },
      success: {
        light: '#86efac',
        main: '#22c55e',
        dark: '#15803d',
      },
      warning: {
        light: '#fcd34d',
        main: '#f59e0b',
        dark: '#d97706',
      },
      error: {
        light: '#fca5a5',
        main: '#ef4444',
        dark: '#dc2626',
      },
      info: {
        light: '#93c5fd',
        main: '#0891b2',
        dark: '#0e7490',
      },
      background: {
        default: '#0f172a', // slate-900
        paper: '#1e293b', // slate-800
        elevated: '#334155', // slate-700
      },
      surface: {
        main: '#1e293b',
        hover: '#334155',
        active: '#475569',
      },
      text: {
        primary: '#f1f5f9', // slate-100
        secondary: '#cbd5e1', // slate-300
        disabled: '#64748b', // slate-500
        inverse: '#0f172a',
      },
      border: {
        light: 'rgba(255, 255, 255, 0.05)',
        main: 'rgba(255, 255, 255, 0.1)',
        dark: 'rgba(255, 255, 255, 0.2)',
      },
      sidebar: {
        background: 'linear-gradient(180deg, #1e40af 0%, #1d4ed8 50%, #2563eb 100%)', // Pure blue gradient
        text: '#ffffff',
        textSecondary: '#dbeafe', // blue-100
        hover: 'rgba(255, 255, 255, 0.15)',
        active: 'rgba(59, 130, 246, 0.3)', // blue accent
        border: 'rgba(255, 255, 255, 0.1)',
        accent: '#3b82f6', // Pure blue
      },
      header: {
        background: 'rgba(255, 255, 255, 0.05)',
        text: '#f1f5f9',
        border: 'rgba(255, 255, 255, 0.1)',
      },
      navigation: {
        background: 'transparent',
        text: '#ffffff',
        hover: 'rgba(255, 255, 255, 0.15)',
        active: 'rgba(59, 130, 246, 0.3)', // blue accent
        activeText: '#ffffff',
        border: 'rgba(255, 255, 255, 0.1)',
      },
    },
  },
};

